import {
  type getBackNotifiche,
  type getGetBackNotificheQueryKey,
  type useGetBackNotifiche,
  type usePutBackNotificheAzioneMassivaRegistraLetturaNotificheCompleto,
  type usePutBackNotificheId,
} from '@framework/api-client/api/backoffice/notifiche/notifiche.ts';
import {
  type getFrontNotifiche,
  type getGetFrontNotificheQueryKey,
  type useGetFrontNotifiche,
  type usePostFrontNotificheIdPresaVisione,
  type usePutFrontNotificheAzioneMassivaRegistraLetturaNotificheCompleto,
} from '@framework/api-client/api/inquiries-compile/notifiche/notifiche.ts';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

export interface INotificationsContext {
  /** Hook per ottenere le notifiche */
  useGetNotifications: typeof useGetFrontNotifiche | typeof useGetBackNotifiche;
  /** Hook per segnare una notifica come letta */
  useMarkAsRead:
    | typeof usePostFrontNotificheIdPresaVisione
    | typeof usePutBackNotificheId;
  /** Chiave della query per ottenere le notifiche */
  getGetNotificationsQueryKey:
    | typeof getGetFrontNotificheQueryKey
    | typeof getGetBackNotificheQueryKey;
  /** Funzione per ottenere le notifiche */
  getNotifications: typeof getFrontNotifiche | typeof getBackNotifiche;
  /** Hook per segnare tutte le notifiche come lette */
  useMarkAllAsRead:
    | typeof usePutFrontNotificheAzioneMassivaRegistraLetturaNotificheCompleto
    | typeof usePutBackNotificheAzioneMassivaRegistraLetturaNotificheCompleto
    | null;
}

export const NotificationsContext = createContext<INotificationsContext>(
  {} as INotificationsContext,
);

export function NotificationsContextProvider(
  props: PropsWithChildren<INotificationsContext>,
) {
  // const {} = props;

  const contextValue = useMemo(
    () => ({
      useGetNotifications: props.useGetNotifications,
      useMarkAsRead: props.useMarkAsRead,
      getGetNotificationsQueryKey: props.getGetNotificationsQueryKey,
      getNotifications: props.getNotifications,
      useMarkAllAsRead: props.useMarkAllAsRead,
    }),
    [
      props.useGetNotifications,
      props.useMarkAsRead,
      props.getGetNotificationsQueryKey,
      props.getNotifications,
      props.useMarkAllAsRead,
    ],
  );

  return (
    <NotificationsContext.Provider value={contextValue}>
      {props.children}
    </NotificationsContext.Provider>
  );
}

/**
 * Hook per accedere al contesto delle comunicazioni
 */
export const useNotificationsContext = () => {
  return useContext(NotificationsContext);
};
